/**
 * Vue I18n configuration
 */
import { createI18n } from 'vue-i18n'
import ar from '@/locales/ar.json'
import en from '@/locales/en.json'
import { DEFAULT_LOCALE } from '@/utils/constants'

type SupportedLocale = 'en' | 'ar'
type MessageTree = Record<string, unknown>

const SUPPORTED_LOCALES: SupportedLocale[] = ['en', 'ar']

/** Locales whose active messages carry tenant overrides. */
const overriddenLocales = new Set<SupportedLocale>()

/** Pristine bundles — every override application starts from these. */
const BASE_MESSAGES: Record<SupportedLocale, MessageTree> = {
  en: en as unknown as MessageTree,
  ar: ar as unknown as MessageTree,
}

const i18n = createI18n({
  legacy: false, // Use Composition API mode
  locale: DEFAULT_LOCALE,
  fallbackLocale: 'en',
  messages: {
    ar,
    en
  },
  globalInjection: true,
  missingWarn: false,
  fallbackWarn: false
})

function cloneBaseMessages(locale: SupportedLocale): MessageTree {
  return JSON.parse(JSON.stringify(BASE_MESSAGES[locale])) as MessageTree
}

function setMessagePath(messages: MessageTree, key: string, value: string): void {
  const parts = key.split('.')
  const leaf = parts.pop()
  if (!leaf) return

  let cursor: MessageTree = messages
  for (const part of parts) {
    const next = cursor[part]
    if (!next || typeof next !== 'object' || Array.isArray(next)) {
      cursor[part] = {}
    }
    cursor = cursor[part] as MessageTree
  }
  cursor[leaf] = value
}

type LocaleMessages = Parameters<typeof i18n.global.setLocaleMessage>[1]

/**
 * Apply tenant overrides on top of the shipped bundles for every locale.
 *
 * Uses `setLocaleMessage` (full replacement) rather than `mergeLocaleMessage` so
 * clearing an override actually restores the default value.
 */
export function applyTenantLabelOverrides(
  overrides: Record<string, Record<string, string>>,
): void {
  for (const locale of SUPPORTED_LOCALES) {
    const values = overrides[locale] ?? {}
    const hasOverrides = Object.keys(values).length > 0

    // No overrides before or now: the locale already shows the shipped bundle.
    if (!hasOverrides && !overriddenLocales.has(locale)) continue

    if (!hasOverrides) {
      overriddenLocales.delete(locale)
      i18n.global.setLocaleMessage(
        locale,
        cloneBaseMessages(locale) as unknown as LocaleMessages,
      )
      continue
    }

    const messages = cloneBaseMessages(locale)
    for (const [key, value] of Object.entries(values)) {
      setMessagePath(messages, key, value)
    }
    overriddenLocales.add(locale)
    i18n.global.setLocaleMessage(locale, messages as unknown as LocaleMessages)
  }
}

/** Restore the shipped bundles (no tenant overrides). */
export function resetTenantLabelOverrides(): void {
  applyTenantLabelOverrides({})
}

export default i18n
